import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, BellOff, BellRing, Loader2, Save, ShieldCheck, UserCog, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { api, errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 227 — Super Admin → Tenant Configuration → Messages (one municipality).
 *  - Citizen messages On / Off  (Off = no citizen message leaves this municipality; every attempt is logged as "skipped")
 *  - Staff messages On / Off    (forgot-password link / code, staff alerts by SMS / WhatsApp / e-mail) — independent
 *  - per message type: Automatic (sent by the system as it happens) or Manual (the Municipal Admin sends it by hand)
 * The Super Admin test allow-lists (Notifications page) are untouched and keep gating every real send.
 */
const MODE_LABEL = { auto: "Automatic", manual: "Manual" };

export const SuperAdminMessagesPanel = ({ code }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get(`/platform/municipalities/${code}/message-settings`).then((r) => {
      setData(r.data);
      setDraft({ citizen_enabled: r.data.citizen_enabled, staff_enabled: r.data.staff_enabled, modes: { ...r.data.modes } });
    }).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  if (!data || !draft) return <Skeleton className="h-64 w-full" data-testid="msgset-loading" />;

  const dirty = draft.citizen_enabled !== data.citizen_enabled || draft.staff_enabled !== data.staff_enabled
    || Object.keys(draft.modes).some((k) => draft.modes[k] !== data.modes[k]);
  const manualCount = Object.values(draft.modes).filter((m) => m === "manual").length;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/platform/municipalities/${code}/message-settings`, draft);
      setData(r.data);
      setDraft({ citizen_enabled: r.data.citizen_enabled, staff_enabled: r.data.staff_enabled, modes: { ...r.data.modes } });
      toast.success("Message settings saved", { description: `${data.municipality?.name || code}: citizen messages ${r.data.citizen_enabled ? "On" : "Off"}, staff messages ${r.data.staff_enabled ? "On" : "Off"}, ${Object.values(r.data.modes).filter((m) => m === "manual").length} type(s) manual.` });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="msgset-panel">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={`shadow-sm ${draft.citizen_enabled ? "" : "border-destructive/50"}`} data-testid="msgset-citizen-card">
          <CardContent className="p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Citizen messages</p>
              <p className="text-xs text-muted-foreground mt-1">
                New demand, payment received, partial payment, due reminders and dues notices, visit updates, Citizen Portal receipts, demand corrected, cheque bounced.
                When <span className="font-medium text-foreground">Off</span>, no citizen message of any kind is sent for this municipality — every attempt is listed as <span className="font-medium text-foreground">Skipped</span> in Notification History.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Label className="text-sm" data-testid="msgset-citizen-state">{draft.citizen_enabled ? "On" : "Off"}</Label>
              <Switch checked={draft.citizen_enabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, citizen_enabled: v }))} data-testid="msgset-citizen-switch" />
            </div>
          </CardContent>
        </Card>
        <Card className={`shadow-sm ${draft.staff_enabled ? "" : "border-destructive/50"}`} data-testid="msgset-staff-card">
          <CardContent className="p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold flex items-center gap-2"><UserCog className="h-4 w-4 text-primary" /> Staff messages</p>
              <p className="text-xs text-muted-foreground mt-1">
                Forgot-password reset link and one-time code, and staff alerts delivered by SMS, WhatsApp or e-mail. Independent of the citizen switch.
                When <span className="font-medium text-foreground">Off</span>, staff are told to contact the Municipal Admin and the attempt is listed as Skipped.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Label className="text-sm" data-testid="msgset-staff-state">{draft.staff_enabled ? "On" : "Off"}</Label>
              <Switch checked={draft.staff_enabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, staff_enabled: v }))} data-testid="msgset-staff-switch" />
            </div>
          </CardContent>
        </Card>
      </div>

      {!draft.citizen_enabled && (
        <p className="text-xs text-destructive flex items-center gap-1.5 border border-destructive/30 rounded-md px-3 py-2" data-testid="msgset-citizen-off-warning">
          <BellOff className="h-3.5 w-3.5" /> Citizen messages are Off — the Automatic / Manual choice below is kept but nothing is sent (manual sends are refused too) until you switch them On again.
        </p>
      )}

      <Card className="shadow-sm" data-testid="msgset-modes-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><BellRing className="h-4 w-4" /> How each message type is sent</CardTitle>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Automatic</span> = the system sends it the moment the event happens.
            <span className="font-medium text-foreground"> Manual</span> = nothing goes out by itself; the Municipal Admin sends it from Notifications → Send Messages (choosing the area or properties, the channels and the number / e-mail per property). Message texts are the Templates — they are not changed here.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="msgset-modes-table">
            <TableHeader>
              <TableRow><TableHead>Message type</TableHead><TableHead className="hidden md:table-cell">What it is</TableHead><TableHead className="w-44">Sent</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.event} data-testid={`msgset-row-${r.event}`}>
                  <TableCell className="text-sm font-medium">{r.label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground hidden md:table-cell">{r.hint}</TableCell>
                  <TableCell>
                    {r.fixed ? (
                      <Badge variant="outline" className="text-[10px]" data-testid={`msgset-fixed-${r.event}`}>{r.mode === "manual" ? "Always manual" : "Always automatic"}</Badge>
                    ) : (
                      <Select value={draft.modes[r.event] || "auto"} onValueChange={(v) => v && setDraft((d) => ({ ...d, modes: { ...d.modes, [r.event]: v } }))} disabled={!draft.citizen_enabled}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`msgset-mode-${r.event}`}><SelectValue>{MODE_LABEL[draft.modes[r.event]] || "Automatic"}</SelectValue></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto" data-testid={`msgset-mode-${r.event}-auto`}>Automatic</SelectItem>
                          <SelectItem value="manual" data-testid={`msgset-mode-${r.event}-manual`}>Manual</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={!dirty || saving} data-testid="msgset-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save message settings
        </Button>
        <Badge variant="secondary" className="text-[10px]" data-testid="msgset-manual-count">{manualCount} manual · {Object.keys(draft.modes).length - manualCount} automatic</Badge>
        {data.updated_at ? (
          <span className="text-[11px] text-muted-foreground" data-testid="msgset-updated">Last changed {fmtDateTime(data.updated_at)}{data.updated_by?.name ? ` by ${data.updated_by.name}` : ""}</span>
        ) : (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="msgset-bootstrapped"><ShieldCheck className="h-3 w-3" /> Not changed yet — these are the municipality's settings as they worked before</span>
        )}
        {dirty && <span className="text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Unsaved changes</span>}
      </div>
    </div>
  );
};

export default SuperAdminMessagesPanel;
